/**
 * Worker Mode flag accessor — Node-safe (no React deps).
 *
 * Reads worker_config.use_worker_mode from Supabase. Used by:
 *   - batchOrchestratorService (browser): if true → delegate to worker
 *   - publishQueueService (browser): same
 *   - In worker runtime: NOT used (worker already IS the worker)
 *
 * Localhost override:
 *   `npm run dev` (import.meta.env.DEV === true) ZAWSZE zwraca false —
 *   worker container nie istnieje lokalnie, klikanie batch w devie
 *   konczy 502 / network error. Browser mode w dev jest jedyna sensowna
 *   sciezka. W produkcji (npm run build) flag czyta z DB jak dotad.
 */
import { supabase } from "@/integrations/supabase/client";

export async function isWorkerModeEnabled(): Promise<boolean> {
  // Localhost: worker container nie istnieje, force browser mode
  // (Vite ustawia import.meta.env.DEV=true tylko w `npm run dev`,
  // produkcyjny build ma DEV=false)
  if (typeof import.meta !== "undefined" && (import.meta as any).env?.DEV) {
    return false;
  }
  try {
    const { data } = await (supabase as any)
      .from("worker_config")
      .select("value")
      .eq("key", "use_worker_mode")
      .maybeSingle();
    return Boolean(data?.value?.value ?? false);
  } catch {
    return false;
  }
}

/**
 * Worker mode dla generowania mapy tematycznej (seeds/nodes).
 * Osobna flaga od `use_worker_mode` (ktora dotyczy Content Engine):
 *   - `use_worker_mode` = true → worker procesuje CE (draft/humanization/etc.)
 *   - `use_worker_mode_map` = true → worker procesuje seedy/nody
 *
 * W dev (localhost) zawsze false — worker nie istnieje.
 */
export async function isWorkerModeMapEnabled(): Promise<boolean> {
  if (typeof import.meta !== "undefined" && (import.meta as any).env?.DEV) {
    return false;
  }
  try {
    const { data } = await (supabase as any)
      .from("worker_config")
      .select("value")
      .eq("key", "use_worker_mode_map")
      .maybeSingle();
    return Boolean(data?.value?.value ?? false);
  } catch {
    return false;
  }
}
